// src/pages/accounting/collateralReceived/components/CollateralReceivedFormDialog.jsx
import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import CollateralDealerDropdown from '../../../../components/common/CollateralDealerDropdown';
import CollateralBankDropdown from '../../../../components/common/CollateralBankDropdown';
import CollateralTypeDropdown from '../../../../components/common/CollateralTypeDropdown';
import CollateralIsReturnedDropdown from '../../../../components/common/CollateralIsReturnedDropdown';

const CollateralReceivedFormDialog = ({
    th,
    visible,
    onHide,
    editMode,
    form,
    errors,
    setField,
    dealerFieldDisabled,
    currencyOptions,
    onSave,
    saving
}) => {
    const invalidClass = (field) => (errors?.[field] ? 'p-invalid w-full' : 'w-full');
    const errorText = (field) => errors?.[field] && <small className="p-error">{errors[field]}</small>;

    return (
        <Dialog
            header={editMode ? th('collateralReceived.editTitle') : th('collateralReceived.addTitle')}
            visible={visible}
            style={{ width: 900, maxWidth: '95vw' }}
            modal
            onHide={onHide}
            contentClassName="p-3"
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button label={th('common.cancel')} icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={saving} />
                    <Button label={editMode ? th('common.update') : th('common.save')} icon="pi pi-check" onClick={onSave} loading={saving} />
                </div>
            }
        >
            <div className="grid" style={{ rowGap: 12 }}>
                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.dealer')}</label>
                    <CollateralDealerDropdown
                        value={form.sapDealerId}
                        selectedLabel={form.dealerName}
                        onChange={(value) => setField('sapDealerId', value)}
                        placeholder={th('collateralReceived.selectDealer')}
                        className={invalidClass('sapDealerId')}
                        disabled={dealerFieldDisabled}
                    />
                    {errorText('sapDealerId')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.numbers')}</label>
                    <InputText value={form.numbers} onChange={(e) => setField('numbers', e.target.value)} className={invalidClass('numbers')} maxLength={50} placeholder={th('collateralReceived.placeholders.numbers')} />
                    {errorText('numbers')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.type')}</label>
                    <CollateralTypeDropdown value={form.type} onChange={(value) => setField('type', value)} placeholder={th('collateralReceived.placeholders.type')} className={invalidClass('type')} />
                    {errorText('type')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">
                        {th('collateralReceived.validity')}
                        <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex gap-4 align-items-center">
                        <div className="flex align-items-center">
                            <RadioButton
                                inputId="validityValid"
                                name="validity"
                                value="GEÇERLİ"
                                onChange={(e) => setField('validity', e.value)}
                                checked={form.validity === 'GEÇERLİ'}
                                disabled
                            />
                            <label htmlFor="validityValid" className="ml-2">
                                {th('collateralReceived.validityValid')}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton
                                inputId="validityInvalid"
                                name="validity"
                                value="GEÇERSİZ"
                                onChange={(e) => setField('validity', e.value)}
                                checked={form.validity === 'GEÇERSİZ'}
                                disabled
                            />
                            <label htmlFor="validityInvalid" className="ml-2">
                                {th('collateralReceived.validityInvalid')}
                            </label>
                        </div>
                    </div>
                    {errorText('validity')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.purchaseDate')}</label>
                    <Calendar value={form.purchaseDate} onChange={(e) => setField('purchaseDate', e.value)} dateFormat="dd/mm/yy" showIcon className={invalidClass('purchaseDate')} appendTo={document.body} />
                    {errorText('purchaseDate')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">
                        {th('collateralReceived.expiryDate')}
                        {form.validity?.trim().toUpperCase() === 'SURELI' && <span className="text-red-500"> *</span>}
                    </label>
                    <Calendar value={form.expiryDate} onChange={(e) => setField('expiryDate', e.value)} dateFormat="dd/mm/yy" showIcon className={invalidClass('expiryDate')} minDate={form.purchaseDate} appendTo={document.body} />
                    {errorText('expiryDate')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.extensionDate')}</label>
                    <Calendar value={form.extensionDate} onChange={(e) => setField('extensionDate', e.value)} dateFormat="dd/mm/yy" showIcon className={invalidClass('extensionDate')} minDate={form.expiryDate || form.purchaseDate} appendTo={document.body} />
                    {errorText('extensionDate')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.enteredAmount')}</label>
                    <div className="flex gap-2">
                        <InputNumber value={form.enteredAmount} onValueChange={(e) => setField('enteredAmount', e.value ?? null)} min={0} mode="decimal" maxFractionDigits={4} className={invalidClass('enteredAmount')} placeholder={th('collateralReceived.placeholders.enteredAmount')} />
                        <Dropdown value={form.currency} onChange={(e) => setField('currency', e.value)} options={currencyOptions} optionLabel="label" optionValue="value" placeholder={th('collateralReceived.currency')} className={errors?.currency ? 'p-invalid' : ''} style={{ minWidth: 110 }} appendTo={document.body} />
                    </div>
                    {errorText('enteredAmount')}
                    {errorText('currency')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.bankName')}</label>
                    <CollateralBankDropdown value={form.bankName} onChange={(value) => setField('bankName', value)} placeholder={th('collateralReceived.placeholders.bankName')} className={invalidClass('bankName')} />
                    {errorText('bankName')}
                </div>

                <div className="col-12 md:col-6">
                    <label className="block mb-2">{th('collateralReceived.bankBranchName')}</label>
                    <InputText value={form.bankBranchName ?? ''} onChange={(e) => setField('bankBranchName', e.target.value)} className={invalidClass('bankBranchName')} maxLength={500} placeholder={th('collateralReceived.placeholders.bankBranchName')} />
                    {errorText('bankBranchName')}
                </div>

                {editMode && (
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">{th('collateralReceived.isReturned')}</label>
                        <CollateralIsReturnedDropdown
                            value={form.isReturned}
                            onChange={(value) => setField('isReturned', value)}
                            placeholder={th('collateralReceived.placeholders.isReturned')}
                            className={invalidClass('isReturned')}
                        />
                        {errorText('isReturned')}
                    </div>
                )}

                {editMode && form.isReturned === '1' && (
                    <div className="col-12 md:col-6">
                        <label className="block mb-2">
                            {th('collateralReceived.returnDate')}
                            <span className="text-red-500"> *</span>
                        </label>
                        <Calendar value={form.returnDate} onChange={(e) => setField('returnDate', e.value)} dateFormat="dd/mm/yy" showIcon className={invalidClass('returnDate')} appendTo={document.body} />
                        {errorText('returnDate')}
                    </div>
                )}

                <div className="col-12">
                    <label className="block mb-2">{th('collateralReceived.reason')}</label>
                    <InputTextarea value={form.reason ?? ''} onChange={(e) => setField('reason', e.target.value)} autoResize rows={2} maxLength={500} className="w-full" placeholder={th('collateralReceived.placeholders.reason')} />
                </div>
            </div>
        </Dialog>
    );
};

export default CollateralReceivedFormDialog;
